/**
 * Hub Definitions - Core hubs (Jobs, CRM, Quote, QA, QDrive, Workforce, Email)
 */

import {
  Briefcase,
  LayoutDashboard,
  BarChart3,
  FileText,
  Calculator,
  CalendarDays,
  Receipt,
  Users,
  Settings,
  ClipboardCheck,
  AlertTriangle,
  Camera,
  CheckCircle,
  HardDrive,
  Clock,
  Search,
  FolderTree,
  Star,
  Upload,
  Mail,
  Inbox,
  Send,
  Archive,
  Tag,
  DollarSign,
  TrendingUp,
  GitCompare,
  LineChart,
  Target,
  Calendar,
  CalendarOff,
  FileEdit,
  Hammer,
  Truck,
  FileUp,
  Bot,
  Zap,
  GitBranch,
  Link,
  Network,
  UserCircle,
  PieChart,
} from 'lucide-react'
import type { HubDefinition } from './types'
import { getHubUrl } from './types'
import type { HubId } from '../../components/types'

export const CORE_HUB_DEFINITIONS: Partial<Record<HubId, HubDefinition>> = {
  jobs: {
    id: 'jobs', name: 'Jobs Hub', shortName: 'Jobs',
    description: 'Project management, job tracking, and operational KPIs',
    icon: Briefcase, baseUrl: getHubUrl('jobs'), category: 'deliver',
    status: 'production', accentColor: 'teal', accentGradient: 'from-teal-500 to-teal-600',
    defaultLandingPage: '/jobs',
    pages: [
      { id: 'dashboard', label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, description: 'Executive overview and KPIs' },
      { id: 'jobs', label: 'Active Jobs', href: '/jobs', icon: Briefcase, description: 'All jobs with health scores', isPrimary: true },
      { id: 'job-detail', label: 'Job Detail', href: '/jobs/:id', icon: Briefcase, description: 'Individual job view' },
      { id: 'reports', label: 'Reports', href: '/reports', icon: BarChart3, description: 'Analytics and reporting' },
      { id: 'monthly-kpi', label: 'Monthly KPI', href: '/reports/monthly-kpi', icon: TrendingUp, description: 'Monthly performance metrics' },
      { id: 'job-performance', label: 'Job Performance', href: '/reports/job-performance', icon: Target, description: 'Individual job analysis' },
      { id: 'wip', label: 'WIP Report', href: '/reports/wip', icon: PieChart, description: 'Work in progress analysis' },
      { id: 'receivables', label: 'Receivables', href: '/reports/receivables', icon: DollarSign, description: 'Accounts receivable' },
      { id: 'prorata', label: 'Pro-rata', href: '/reports/pro-rata', icon: Calculator, description: 'Pro-rata calculations' },
      { id: 'daysheets', label: 'Day Sheets', href: '/daysheets', icon: CalendarDays, description: 'Daily labor tracking' },
      { id: 'invoices', label: 'Invoices', href: '/invoices', icon: Receipt, description: 'Invoice management' },
      { id: 'team', label: 'Team', href: '/team', icon: Users, description: 'Team management' },
      { id: 'org-structure', label: 'Org Structure', href: '/organisation', icon: Network, description: 'Organisation settings' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'System configuration' },
    ],
  },

  crm: {
    id: 'crm', name: 'CRM Hub', shortName: 'CRM',
    description: 'Customer relationships, quote pipeline, and sales analytics',
    icon: Users, baseUrl: getHubUrl('crm'), category: 'acquire',
    status: 'production', accentColor: 'teal', accentGradient: 'from-teal-500 to-teal-600',
    defaultLandingPage: '/quotes',
    pages: [
      { id: 'inbox', label: 'Inbox', href: '/inbox', icon: Inbox, description: 'AI-prioritized action items' },
      { id: 'dashboard', label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, description: 'CRM overview' },
      { id: 'quotes', label: 'All Quotes', href: '/quotes', icon: FileText, description: 'Quote list with filters', isPrimary: true },
      { id: 'quote-detail', label: 'Quote Detail', href: '/quotes/:id', icon: FileEdit, description: 'Individual quote view' },
      { id: 'pipeline', label: 'Pipeline', href: '/pipeline', icon: GitBranch, description: 'Kanban pipeline view' },
      { id: 'customers', label: 'All Customers', href: '/customers', icon: Users, description: 'Customer directory' },
      { id: 'customer-detail', label: 'Customer Detail', href: '/customers/:id', icon: UserCircle, description: 'Customer profile' },
      { id: 'match-review', label: 'Match Review', href: '/customers/match', icon: GitCompare, description: 'Duplicate matching' },
      { id: 'reports', label: 'Reports', href: '/reports', icon: BarChart3, description: 'CRM analytics' },
      { id: 'win-rate', label: 'Win Rate', href: '/reports/win-rate', icon: TrendingUp, description: 'Quote conversion analysis' },
      { id: 'pipeline-report', label: 'Pipeline Report', href: '/reports/pipeline', icon: LineChart, description: 'Pipeline analytics' },
      { id: 'forecast', label: 'Forecast', href: '/reports/forecast', icon: Target, description: 'Revenue forecasting' },
    ],
  },

  quote: {
    id: 'quote', name: 'Quote Builder', shortName: 'Builder',
    description: 'AI-assisted quote creation with pricelists and supplier quotes',
    icon: Calculator, baseUrl: getHubUrl('quote'), category: 'acquire',
    status: 'production', accentColor: 'amber', accentGradient: 'from-amber-500 to-orange-600',
    defaultLandingPage: '/builder',
    pages: [
      { id: 'builder', label: 'Quote Builder', href: '/builder', icon: Hammer, description: 'Create new quote', isPrimary: true },
      { id: 'builder-new', label: 'New Quote', href: '/builder/new', icon: FileEdit, description: 'Start fresh quote' },
      { id: 'builder-edit', label: 'Edit Quote', href: '/builder/:id', icon: FileEdit, description: 'Edit existing quote' },
      { id: 'quotes', label: 'All Quotes', href: '/quotes', icon: FileText, description: 'Browse all quotes' },
      { id: 'pricelist', label: 'Pricelist', href: '/pricelist', icon: DollarSign, description: 'Product & pricing master' },
      { id: 'suppliers', label: 'Suppliers', href: '/suppliers', icon: Truck, description: 'Supplier directory' },
      { id: 'supplier-quotes', label: 'Supplier Quotes', href: '/supplier-quotes', icon: FileUp, description: 'Upload & extract supplier quotes' },
      // Payment Claims is intentionally hidden from the registry while the
      // backend `claims` Django app is disabled (`/api/claims/` returns 404,
      // which made the sidebar link land on a never-resolving spinner — see
      // AGENT_HANDBOOK note "claims/ — Payment claims (DISABLED — no
      // migrations)" and FE !2805 for the prior local-nav patch that didn't
      // take effect because the sidebar reads from THIS registry, not
      // apps/quote/src/config/navigation.ts). Re-enable as a one-line
      // uncomment once the backend `claims` app is reactivated.
      // { id: 'claims', label: 'Payment Claims', href: '/claims', icon: Receipt, description: 'Progress claims' },
      { id: 'templates', label: 'Templates', href: '/templates', icon: FileText, description: 'Quote templates' },
      { id: 'formula-registry', label: 'Formula Registry', href: '/formulas', icon: Calculator, description: 'Calculation formulas' },
      { id: 'margin-analysis', label: 'Margin Analysis', href: '/margins', icon: TrendingUp, description: 'Margin analytics' },
      { id: 'ai-suggestions', label: 'AI Suggestions', href: '/ai', icon: Bot, description: 'AI pricing assistance' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'Builder settings' },
    ],
  },

  qa: {
    id: 'qa', name: 'QA Hub', shortName: 'QA',
    description: 'Quality assurance, inspections, NCRs, and evidence management',
    icon: ClipboardCheck, baseUrl: getHubUrl('qa'), category: 'deliver',
    status: 'production', accentColor: 'green', accentGradient: 'from-green-500 to-emerald-600',
    defaultLandingPage: '/records',
    pages: [
      { id: 'dashboard', label: 'Dashboard', href: '/', icon: LayoutDashboard, description: 'QA overview' },
      { id: 'inspections', label: 'Inspections', href: '/records', icon: ClipboardCheck, description: 'All inspection records', isPrimary: true },
      { id: 'inspection-detail', label: 'Inspection', href: '/records/:id', icon: ClipboardCheck, description: 'Individual inspection' },
      { id: 'ncrs', label: 'NCRs/Defects', href: '/ncrs', icon: AlertTriangle, description: 'Non-conformance reports' },
      { id: 'ncr-detail', label: 'NCR Detail', href: '/ncrs/:id', icon: AlertTriangle, description: 'NCR details' },
      { id: 'templates', label: 'Templates', href: '/templates', icon: FileText, description: 'Checklist templates' },
      { id: 'evidence', label: 'Evidence', href: '/evidence', icon: Camera, description: 'Photo evidence library' },
      { id: 'signoffs', label: 'Sign-offs', href: '/signoffs', icon: CheckCircle, description: 'Approval workflows' },
      { id: 'reports', label: 'Reports', href: '/reports', icon: BarChart3, description: 'QA analytics' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'QA configuration' },
    ],
  },

  qdrive: {
    id: 'qdrive', name: 'QDrive', shortName: 'QDrive',
    description: 'Shared drive organized by projects with AI-powered search',
    icon: HardDrive, baseUrl: getHubUrl('qdrive'), category: 'manage',
    status: 'production', accentColor: 'sky', accentGradient: 'from-sky-500 to-cyan-600',
    defaultLandingPage: '/dashboard',
    pages: [
      { id: 'dashboard', label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, description: 'QDrive overview' },
      { id: 'by-job', label: 'Files by Job', href: '/files/job', icon: Briefcase, description: 'Project-organized files', isPrimary: true },
      { id: 'job-files', label: 'Job Files', href: '/files/job/:jobId', icon: FolderTree, description: 'Individual job files' },
      { id: 'by-type', label: 'Files by Type', href: '/files/type', icon: FileText, description: 'Document type view' },
      { id: 'recent', label: 'Recent Files', href: '/recent', icon: Clock, description: 'Recently accessed' },
      { id: 'ai-search', label: 'AI Search', href: '/search', icon: Search, description: 'Intelligent document search' },
      { id: 'folders', label: 'Browse Folders', href: '/folders/root', icon: FolderTree, description: 'Full folder tree' },
      { id: 'starred', label: 'Starred', href: '/starred', icon: Star, description: 'Favorited files' },
      { id: 'my-drive', label: 'My Drive', href: '/my-drive', icon: HardDrive, description: 'Personal files' },
      { id: 'upload', label: 'Upload Files', href: '/uploads', icon: Upload, description: 'Bulk upload' },
      { id: 'folder-ingestor', label: 'Folder Ingestor', href: '/ingest/folder', icon: FolderTree, description: 'Import folder structures' },
      { id: 'smart-ingestor', label: 'Smart Ingestor', href: '/ingest/smart', icon: Bot, description: 'AI-powered import' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'QDrive configuration' },
    ],
  },

  workforce: {
    id: 'workforce', name: 'Workforce Hub', shortName: 'Workforce',
    description: 'Staff scheduling, timesheets, capacity planning, and leave',
    icon: Calendar, baseUrl: getHubUrl('workforce'), category: 'deliver',
    status: 'development', accentColor: 'teal', accentGradient: 'from-teal-500 to-cyan-600',
    defaultLandingPage: '/allocation',
    pages: [
      { id: 'dashboard', label: 'Dashboard', href: '/', icon: LayoutDashboard, description: 'Workforce overview' },
      { id: 'allocation', label: 'Allocation Board', href: '/allocation', icon: Briefcase, description: 'Drag-drop scheduling', isPrimary: true },
      { id: 'schedule', label: 'Schedule', href: '/schedule', icon: Calendar, description: 'Calendar view' },
      { id: 'timesheets', label: 'Timesheets', href: '/timesheets', icon: Clock, description: 'Time entry & approval' },
      { id: 'timesheet-detail', label: 'Timesheet', href: '/timesheets/:id', icon: Clock, description: 'Individual timesheet' },
      { id: 'employees', label: 'Employees', href: '/employees', icon: Users, description: 'Staff directory' },
      { id: 'employee-detail', label: 'Employee', href: '/employees/:id', icon: UserCircle, description: 'Employee profile' },
      { id: 'leave', label: 'Leave', href: '/leave', icon: CalendarOff, description: 'Leave requests' },
      { id: 'capacity', label: 'Capacity Planner', href: '/capacity', icon: BarChart3, description: 'Capacity analysis' },
      { id: 'ai-insights', label: 'AI Insights', href: '/insights', icon: Bot, description: 'Workforce intelligence' },
      { id: 'labour-costing', label: 'Labour Costing', href: '/costing', icon: DollarSign, description: 'Labor cost analysis' },
      { id: 'reports', label: 'Reports', href: '/reports', icon: BarChart3, description: 'Workforce reports' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'Workforce configuration' },
    ],
  },

  email: {
    id: 'email', name: 'Emails', shortName: 'Emails',
    description: 'Email capture, AI classification, and job attribution',
    icon: Mail, baseUrl: getHubUrl('email'), category: 'manage',
    status: 'development', accentColor: 'rose', accentGradient: 'from-rose-500 to-pink-600',
    defaultLandingPage: '/inbox',
    pages: [
      { id: 'inbox', label: 'Inbox', href: '/', icon: Inbox, description: 'Email inbox with classification', isPrimary: true },
      { id: 'by-job', label: 'By Job', href: '/by-job', icon: Briefcase, description: 'Emails organized by project' },
      { id: 'job-emails', label: 'Job Emails', href: '/by-job/:jobId', icon: Mail, description: 'Individual job emails' },
      { id: 'sent', label: 'Sent', href: '/sent', icon: Send, description: 'Sent emails' },
      { id: 'archive', label: 'Archive', href: '/archive', icon: Archive, description: 'Archived emails' },
      { id: 'search', label: 'Search', href: '/search', icon: Search, description: 'Email search' },
      { id: 'classification', label: 'Classification', href: '/classification', icon: Tag, description: 'AI classification rules' },
      { id: 'attribution', label: 'Attribution', href: '/attribution', icon: Link, description: 'Job attribution settings' },
      { id: 'templates', label: 'Templates', href: '/templates', icon: FileText, description: 'Email templates' },
      { id: 'rules', label: 'Rules', href: '/rules', icon: Zap, description: 'Automation rules' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'Email hub settings' },
    ],
  },
}
